import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Snackbar,
  Stepper,
  Step,
  StepLabel,
  Stack,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { sha256 } from 'js-sha256';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../context/AuthContext';
import logoImage from '../assets/logo2.png';
import { markLoginAnnouncementsPending } from '../utils/loginAnnouncements';
import { DATA_INTELLIGENZ_URL } from '../constants/brand';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

function LoginPage() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [email, setEmail] = useState('');
  const [projectId, setProjectId] = useState('');
  const [password, setPassword] = useState('');
  const [showPasswordStep, setShowPasswordStep] = useState(false);
  const [showPasswordText, setShowPasswordText] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoading: authLoading } = useAuth();

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, location]);

  const handleInitialSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/validate-step1`, {
        email,
        projectId,
      });

      if (response.status === 200) {
        setShowPasswordStep(true);
      } else {
        setError(response.data.message || 'Error desconocido durante la validación inicial.');
      }
    } catch (err) {
      if (err.response) {
        setError(err.response.data.message || 'Error: Combinación de correo y proyecto inválida.');
      } else {
        setError('Error de conexión con el servidor.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordConfirm = async () => {
    setOpenConfirmDialog(false);
    setIsLoading(true);
    setError('');
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/request-otp`, { email, projectId });
      if (response.status === 200) {
        setSnackbarMessage(response.data.message || 'Se ha enviado una clave temporal a su correo. Por favor, utilícela para iniciar sesión.');
        setSnackbarOpen(true);
        setIsLoading(false);
        setTimeout(() => {
          setShowPasswordStep(false);
          setPassword('');
          navigate('/login', { replace: true });
        }, 3000);
      } else {
        setError(response.data.message || 'Error al solicitar la clave temporal.');
        setIsLoading(false);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error al conectar con el servidor para solicitar la clave temporal.');
      setIsLoading(false);
    }
  };

  const handlePasswordValidation = async (event) => {
    if (event) event.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const hashedPassword = await sha256(password);
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/validate-step2`,
        { hashedPassword },
        { withCredentials: true }
      );
      login(response.data);
      markLoginAnnouncementsPending();
      navigate('/');
    } catch (err) {
      if (err.response && err.response.status === 401) {
        const errorMessage = err.response.data?.message || 'Contraseña incorrecta. Por favor, verifique sus credenciales.';
        setError(errorMessage);
        setPassword('');
      } else {
        const errorMessage = err.response?.data?.message || 'Error al validar la contraseña. Intente nuevamente.';
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackStep = () => {
    setShowPasswordStep(false);
    setPassword('');
    setError('');
  };

  const formContent = (
    <Stack spacing={2.5} sx={{ width: '100%', maxWidth: 400 }}>
      <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
          {showPasswordStep ? 'Casi listo' : 'Bienvenido'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {showPasswordStep
            ? 'Ingresa tu contraseña para acceder al panel.'
            : 'Accede con tus credenciales de Kepler.'}
        </Typography>
      </Box>

      <Stepper activeStep={showPasswordStep ? 1 : 0} alternativeLabel sx={{ mt: 1 }}>
        <Step>
          <StepLabel>Identificación</StepLabel>
        </Step>
        <Step>
          <StepLabel>Contraseña</StepLabel>
        </Step>
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ fontWeight: 500 }}>
          {error}
        </Alert>
      )}

      <Box
        component="form"
        onSubmit={showPasswordStep ? handlePasswordValidation : handleInitialSubmit}
        noValidate
      >
        <Stack spacing={2}>
          <TextField
            required
            fullWidth
            id="email"
            label="Correo electrónico"
            name="email"
            autoComplete="email"
            autoFocus={!showPasswordStep}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={showPasswordStep || isLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlinedIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            required
            fullWidth
            id="projectId"
            label="Número de proyecto"
            name="projectId"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            disabled={showPasswordStep || isLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessOutlinedIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          {showPasswordStep && (
            <>
              <TextField
                required
                fullWidth
                name="password"
                label="Contraseña"
                type={showPasswordText ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPasswordText(!showPasswordText)}
                        edge="end"
                        size="small"
                      >
                        {showPasswordText ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                  size="small"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBackStep}
                  disabled={isLoading}
                  sx={{ color: 'text.secondary' }}
                >
                  Cambiar correo
                </Button>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => setOpenConfirmDialog(true)}
                  disabled={isLoading}
                  sx={{ color: 'secondary.main', fontWeight: 600 }}
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </Box>
            </>
          )}

          <Box sx={{ position: 'relative', mt: 1 }}>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="secondary"
              size="large"
              sx={{ py: 1.4, fontWeight: 700, fontSize: 15 }}
              disabled={
                isLoading ||
                (!showPasswordStep && (!email || !projectId)) ||
                (showPasswordStep && !password)
              }
            >
              {showPasswordStep ? 'Iniciar sesión' : 'Continuar'}
            </Button>
            {isLoading && (
              <CircularProgress
                size={24}
                color="secondary"
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Box>
        </Stack>
      </Box>

      <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 2 }}>
        ¿Necesitas ayuda?{' '}
        <Link
          component="button"
          type="button"
          onClick={() => navigate('/about')}
          sx={{ fontWeight: 600 }}
        >
          Conoce más sobre la plataforma
        </Link>
      </Typography>
      <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 1 }}>
        Desarrollado por{' '}
        <Link
          href={DATA_INTELLIGENZ_URL}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ fontWeight: 600 }}
        >
          Data Intelligenz
        </Link>
      </Typography>
    </Stack>
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          minHeight: { xs: 'auto', md: 'calc(100vh - 200px)' },
          mx: { xs: -3, md: -3 },
          mt: -3,
          mb: -3,
        }}
      >
        {/* Panel izquierdo: branding */}
        {isDesktop && (
          <Box
            sx={{
              flex: '0 0 45%',
              bgcolor: 'primary.main',
              color: 'common.white',
              p: 6,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              position: 'relative',
              overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '-30%',
                right: '-30%',
                width: '80%',
                height: '80%',
                bgcolor: 'secondary.main',
                opacity: 0.08,
                borderRadius: '50%',
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                bottom: '-20%',
                left: '-10%',
                width: '60%',
                height: '60%',
                bgcolor: 'secondary.main',
                opacity: 0.06,
                borderRadius: '50%',
              },
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box
                component="img"
                src={logoImage}
                alt="Kepler"
                sx={{ height: 56, mb: 4 }}
              />
              <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1.15, mb: 2 }}>
                Aprueba con
                <br />
                <Box component="span" sx={{ color: 'secondary.main' }}>
                  confianza.
                </Box>
              </Typography>
              <Typography variant="body1" sx={{ color: 'grey.300', maxWidth: 380 }}>
                Plataforma corporativa para autorizar órdenes de servicio Kepler con trazabilidad y seguridad.
              </Typography>
            </Box>

            <Stack spacing={1.5} sx={{ position: 'relative', zIndex: 1, mt: 4 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CheckCircleIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: 'grey.300' }}>
                  Autenticación con credenciales Kepler
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CheckCircleIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: 'grey.300' }}>
                  Trazabilidad por hashes verificables
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CheckCircleIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: 'grey.300' }}>
                  Visibilidad integral de órdenes y estatus
                </Typography>
              </Stack>
            </Stack>
          </Box>
        )}

        {/* Panel derecho: formulario */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 3, md: 6 },
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: { xs: 2, md: 0 } }}>
            {formContent}
          </Box>
        </Box>
      </Box>

      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        aria-labelledby="alert-dialog-title"
      >
        <DialogTitle id="alert-dialog-title">Confirmar recuperación de contraseña</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se enviará una clave temporal a la dirección de correo: <strong>{email}</strong>.
            Deberá usar esta clave para iniciar sesión nuevamente. ¿Desea continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleForgotPasswordConfirm}
            variant="contained"
            color="secondary"
            disabled={isLoading}
          >
            Confirmar
            {isLoading && <CircularProgress size={20} sx={{ ml: 1 }} color="inherit" />}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}

export default LoginPage;
